import { baseApi } from "api/BaseApi";
import { PartialWith } from "api/common/utils/type.utils";
import { getISOCurrentDate } from "utils/timeUtils";

import { Ear, EarCreationPayload, EarUpdate, EarWithFiles } from "./ear.types";

const EAR_BASE_URL = "ear";
const EAR_RESPONSE_BASE_URL = "ear/update";

const buildEarFormData = ({
  ear,
  earFile,
  currentUser,
  productId,
}: EarCreationPayload): FormData => {
  const earData = new FormData();

  if (earFile.file) {
    //if ear file, we add to submitted data
    earData.append("attachments_file", earFile.file);
    earData.append("attachments_source_type", earFile.file_source_type);
    earData.append("attachments_uploaded", earFile.uploaded_on);
    earData.append("active", "true");
    earData.append("file_label", earFile.file_label);
    earData.append("file_display_name", "Entropy Assessment Report (EAR)");
    earData.append("private", "true");
  }

  earData.append("pid", productId);
  // @ts-ignore // TODO: remove once user types merged in
  earData.append("creator", currentUser.id);
  // @ts-ignore // TODO: remove once user types merged in
  earData.append("editor", currentUser.id);
  earData.append("entrydate", getISOCurrentDate());
  earData.append("status", "Draft");
  earData.append("apriori", ear.apriori.toString());
  earData.append("reqd", ear.reqd.toString());
  earData.append("blurba", ear.blurba);
  earData.append("just", ear.just);

  return earData;
};

const earApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getEarsAndFilesByProductId: build.query<EarWithFiles[], number>({
      query: (productId) => ({
        //TODO: I could probably clean this response up (files in particular) since we are only returning on product form and product form read only
        url: `${EAR_BASE_URL}/ears_and_files_by_product_id/`,
        params: {
          product_id: productId,
        },
        method: "GET",
      }),
    }),

    createEar: build.mutation<Ear, EarCreationPayload>({
      query: (body) => ({
        url: `${EAR_BASE_URL}/`,
        method: "POST",
        body: buildEarFormData(body),
      }),
      invalidatesTags: ["EARS"],
    }),

    updateEar: build.mutation<Ear, PartialWith<Ear, "earid" | "pid">>({
      query: (ear) => ({
        url: `${EAR_BASE_URL}/${ear.earid}/`,
        method: "PUT",
        body: ear,
      }),
      invalidatesTags: ["EARS"],
    }),
    deleteEar: build.mutation<Ear, { earid: number }>({
      query: ({ earid }) => ({
        url: `${EAR_BASE_URL}/${earid}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["EARS"],
    }),

    createEarResponse: build.mutation<EarUpdate, EarUpdate>({
      query: (ear) => ({
        url: `${EAR_RESPONSE_BASE_URL}/`,
        method: "POST",
        body: ear,
      }),
    }),

    updateEarResponse: build.mutation<
      EarUpdate,
      PartialWith<EarUpdate, "upid">
    >({
      query: (ear) => ({
        url: `${EAR_RESPONSE_BASE_URL}/${ear.upid}/`,
        method: "PUT",
        body: ear,
      }),
    }),
  }),

  overrideExisting: false,
});

export const {
  useGetEarsAndFilesByProductIdQuery,
  useCreateEarMutation,
  useUpdateEarMutation,
  useDeleteEarMutation,
  useCreateEarResponseMutation,
  useUpdateEarResponseMutation,
} = earApi;
